import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Delaunay } from "d3-delaunay";
import logo from "@/assets/emblem-logo.jpg";
import "./PageTransition.css";

type Shard = { clip: string; tx: number; ty: number; rot: number; delay: number };

const IS_MOBILE =
  typeof navigator !== "undefined" &&
  (/Android|iPhone|iPad|iPod|Mobile|Silk/i.test(navigator.userAgent || "") ||
    (typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)")?.matches) ||
    (typeof window !== "undefined" && window.innerWidth < 820));

function buildShards(count: number): Shard[] {
  const seeds: [number, number][] = [];
  for (let i = 0; i < count; i++) {
    let x: number, y: number;
    if (i < count * 0.6) {
      // denser cluster around the center (the logo) so the shatter clearly
      // originates there and radiates outward
      const ang = Math.random() * Math.PI * 2;
      const rad = Math.pow(Math.random(), 0.8) * 32;
      x = 50 + Math.cos(ang) * rad * 1.4;
      y = 50 + Math.sin(ang) * rad;
    } else {
      x = Math.random() * 100;
      y = Math.random() * 100;
    }
    seeds.push([Math.max(0, Math.min(100, x)), Math.max(0, Math.min(100, y))]);
  }
  const voronoi = Delaunay.from(seeds).voronoi([0, 0, 100, 100]);
  const shards: Shard[] = [];
  const unit = IS_MOBILE ? 1.1 : 1.0;
  for (let i = 0; i < seeds.length; i++) {
    const cell = voronoi.cellPolygon(i);
    if (!cell) continue;
    const clip =
      "polygon(" + cell.map((p: number[]) => `${p[0].toFixed(2)}% ${p[1].toFixed(2)}%`).join(", ") + ")";
    let cx = 0, cy = 0;
    cell.forEach((p: number[]) => { cx += p[0]; cy += p[1]; });
    cx /= cell.length; cy /= cell.length;
    const dx = cx - 50, dy = cy - 50;
    // use the TRUE max possible distance (corner ~71) so distNorm spans the
    // whole screen evenly — gives a clean, even wavefront from center to corners
    const dist = Math.sqrt(dx * dx + dy * dy);
    const distNorm = Math.min(dist / 71, 1); // 0 at center, 1 at far corner
    // fly strictly along the radial (center -> piece) direction so the whole
    // field reads as bursting outward FROM the middle. Outer pieces travel
    // farther; jitter is kept small and perpendicular so nothing drifts sideways.
    const safeDist = dist || 1;
    const ux = dx / safeDist, uy = dy / safeDist; // unit radial direction
    const reach = (50 + distNorm * 80 + Math.random() * 20) * unit;
    const jitter = (Math.random() - 0.5) * 8;     // small, subtle
    const tx = ux * reach - uy * jitter;          // jitter is perpendicular
    const ty = uy * reach + ux * jitter + 4;
    const rot = (Math.random() - 0.5) * 120;
    // delay scales almost-linearly with distance so the break travels outward
    // as a clean ripple: dead center launches at ~0, far corners last. Minimal
    // random noise keeps the wavefront crisp rather than scattered.
    const maxStagger = IS_MOBILE ? 0.8 : 1.1;
    const delay = Math.pow(distNorm, 0.95) * maxStagger + Math.random() * 0.02;
    shards.push({ clip, tx, ty, rot, delay });
  }
  return shards;
}

type Phase = "idle" | "carve" | "flare" | "shatter";

/**
 * Classical-emblem page transition:
 *   1. screen covers; a carved Roman medallion (geometric sigil + logo mark)
 *      engraves itself into the stone
 *   2. it flares with gold light; the route changes underneath
 *   3. the emblem + pane shatter into Voronoi pieces, revealing the new page
 *
 * Desktop gets the full carved look; mobile uses fewer shards, stable vw
 * units, simpler effects, and a hard safety timer so it can never get stuck.
 */
export default function PageTransition({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [shards, setShards] = useState<Shard[]>([]);
  const [phase, setPhase] = useState<Phase>("idle");
  const timers = useRef<number[]>([]);
  const animating = useRef(false);

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement)?.closest?.("a") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (
        !href.startsWith("/") || href.startsWith("//") ||
        a.target === "_blank" || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0
      ) return;

      const hashIdx = href.indexOf("#");
      const targetPath = hashIdx >= 0 ? href.slice(0, hashIdx).replace(/\/$/, "") || "/" : href;
      const targetHash = hashIdx >= 0 ? href.slice(hashIdx + 1) : "";
      const currentPath = location.pathname.replace(/\/$/, "") || "/";
      if (targetPath === currentPath) return;

      if (animating.current) { e.preventDefault(); return; }
      e.preventDefault();
      e.stopPropagation();
      animating.current = true;

      setShards(buildShards(IS_MOBILE ? 24 : 220));
      setPhase("carve");
      document.body.classList.add("pt-active");
      clearTimers();

      const doNav = () => {
        if (targetHash) navigate(targetPath, { state: { faScrollTo: targetHash } });
        else navigate(href);
      };

      // timeline: sigil forms slowly -> shatters right as it completes
      const flareMs = IS_MOBILE ? 760 : 920;    // sigil takes its time drawing in
      const navMs = flareMs + 40;                // swap underneath (page starts loading)
      const shatterMs = flareMs + 420;           // wait for the new page to paint, THEN shatter
      const endMs = shatterMs + (IS_MOBILE ? 1700 : 2700);

      timers.current.push(window.setTimeout(() => setPhase("flare"), flareMs));
      timers.current.push(window.setTimeout(doNav, navMs));
      timers.current.push(window.setTimeout(() => setPhase("shatter"), shatterMs));
      timers.current.push(window.setTimeout(() => { setPhase("idle"); animating.current = false; document.body.classList.remove("pt-active"); }, endMs));
      // hard safety net
      timers.current.push(window.setTimeout(() => { setPhase("idle"); animating.current = false; document.body.classList.remove("pt-active"); }, endMs + 1200));
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [location.pathname, navigate]);

  useEffect(() => () => clearTimers(), []);

  const overlayPhaseClass =
    phase === "carve" ? "is-carve" :
    phase === "flare" ? "is-flare" :
    phase === "shatter" ? "is-shatter" : "";

  return (
    <>
      <div className="pt-page">{children}</div>

      {phase !== "idle" && (
        <div
          className={`pt-overlay ${IS_MOBILE ? "pt-overlay--mobile" : ""} ${overlayPhaseClass}`}
          aria-hidden="true"
        >
          {/* full-screen sigil — lines radiating across the whole viewport */}
          <svg className="pt-grid" viewBox="0 0 100 100" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* radiating spokes from center to edges */}
            <path className="pt-line pt-g1" d="M50 50 L50 0" />
            <path className="pt-line pt-g2" d="M50 50 L50 100" />
            <path className="pt-line pt-g3" d="M50 50 L0 50" />
            <path className="pt-line pt-g4" d="M50 50 L100 50" />
            <path className="pt-line pt-g5" d="M50 50 L0 0" />
            <path className="pt-line pt-g6" d="M50 50 L100 0" />
            <path className="pt-line pt-g7" d="M50 50 L0 100" />
            <path className="pt-line pt-g8" d="M50 50 L100 100" />
            {/* concentric rings reaching toward the edges */}
            <circle className="pt-line pt-gr1" cx="50" cy="50" r="22" />
            <circle className="pt-line pt-gr2" cx="50" cy="50" r="40" />
            <circle className="pt-line pt-gr3" cx="50" cy="50" r="62" />
          </svg>

          {/* the carved Roman emblem, centered */}
          <div className="pt-emblem">
            <svg className="pt-sigil" viewBox="-10 -10 220 220" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* outer engraved ring */}
              <circle className="pt-line pt-line--ring" cx="100" cy="100" r="92" />
              <circle className="pt-line pt-line--ring2" cx="100" cy="100" r="74" />
              {/* 12-pointed radiating star (echoes the petals-and-star figure) */}
              <path className="pt-line pt-d1" d="M100.00 4.00 L117.08 36.25 L148.00 16.86 L146.67 53.33 L183.14 52.00 L163.75 82.92 L196.00 100.00 L163.75 117.08 L183.14 148.00 L146.67 146.67 L148.00 183.14 L117.08 163.75 L100.00 196.00 L82.92 163.75 L52.00 183.14 L53.33 146.67 L16.86 148.00 L36.25 117.08 L4.00 100.00 L36.25 82.92 L16.86 52.00 L53.33 53.33 L52.00 16.86 L82.92 36.25 Z" />
              {/* ring of rounded petals */}
              <path className="pt-line pt-arc1" d="M100.00 48.00 A20 20 0 0 1 136.77 63.23" />
              <path className="pt-line pt-arc2" d="M136.77 63.23 A20 20 0 0 1 152.00 100.00" />
              <path className="pt-line pt-arc1" d="M152.00 100.00 A20 20 0 0 1 136.77 136.77" />
              <path className="pt-line pt-arc2" d="M136.77 136.77 A20 20 0 0 1 100.00 152.00" />
              <path className="pt-line pt-arc1" d="M100.00 152.00 A20 20 0 0 1 63.23 136.77" />
              <path className="pt-line pt-arc2" d="M63.23 136.77 A20 20 0 0 1 48.00 100.00" />
              <path className="pt-line pt-arc1" d="M48.00 100.00 A20 20 0 0 1 63.23 63.23" />
              <path className="pt-line pt-arc2" d="M63.23 63.23 A20 20 0 0 1 100.00 48.00" />
              {/* fine radiating center burst */}
              <path className="pt-line pt-d2" d="M100 100 L100.00 60.00" />
              <path className="pt-line pt-d3" d="M100 100 L110.35 61.36" />
              <path className="pt-line pt-d4" d="M100 100 L120.00 65.36" />
              <path className="pt-line pt-d2" d="M100 100 L128.28 71.72" />
              <path className="pt-line pt-d3" d="M100 100 L134.64 80.00" />
              <path className="pt-line pt-d4" d="M100 100 L138.64 89.65" />
              <path className="pt-line pt-d2" d="M100 100 L140.00 100.00" />
              <path className="pt-line pt-d3" d="M100 100 L138.64 110.35" />
              <path className="pt-line pt-d4" d="M100 100 L134.64 120.00" />
              <path className="pt-line pt-d2" d="M100 100 L128.28 128.28" />
              <path className="pt-line pt-d3" d="M100 100 L120.00 134.64" />
              <path className="pt-line pt-d4" d="M100 100 L110.35 138.64" />
              <path className="pt-line pt-d2" d="M100 100 L100.00 140.00" />
              <path className="pt-line pt-d3" d="M100 100 L89.65 138.64" />
              <path className="pt-line pt-d4" d="M100 100 L80.00 134.64" />
              <path className="pt-line pt-d2" d="M100 100 L71.72 128.28" />
              <path className="pt-line pt-d3" d="M100 100 L65.36 120.00" />
              <path className="pt-line pt-d4" d="M100 100 L61.36 110.35" />
              <path className="pt-line pt-d2" d="M100 100 L60.00 100.00" />
              <path className="pt-line pt-d3" d="M100 100 L61.36 89.65" />
              <path className="pt-line pt-d4" d="M100 100 L65.36 80.00" />
              <path className="pt-line pt-d2" d="M100 100 L71.72 71.72" />
              <path className="pt-line pt-d3" d="M100 100 L80.00 65.36" />
              <path className="pt-line pt-d4" d="M100 100 L89.65 61.36" />
              {/* inner ring framing the logo */}
              <circle className="pt-line pt-line--inner" cx="100" cy="100" r="46" />
            </svg>
            <div className="pt-emblem__logo" style={{ backgroundImage: `url(${logo})` }} />
          </div>

          {/* shatter shards (only animate in the shatter phase) */}
          {shards.map((s, i) => (
            <div
              key={i}
              className="pt-shard"
              style={
                {
                  clipPath: s.clip,
                  WebkitClipPath: s.clip,
                  "--tx": IS_MOBILE ? `${s.tx}vw` : `${s.tx}vmax`,
                  "--ty": IS_MOBILE ? `${s.ty}vw` : `${s.ty}vmax`,
                  "--rot": `${s.rot}deg`,
                  "--delay": `${s.delay}s`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}
    </>
  );
}
